/**
 * Theme switching ships dormant.
 *
 * The DS dark layer and the provider are wired, but production is pinned to
 * light and the Appearance control is hidden until the surface conversion is
 * far enough along to look finished. Roughly 560 files still paint fixed
 * colors (`bg-white`, `bg-nf-ds-gray-50`), so a dark theme today renders as a
 * half-converted page rather than a dark one.
 *
 * Opt in per environment with:
 *
 *   NEXT_PUBLIC_ENABLE_THEME_SWITCHER="true"
 *
 * Off by default, matching NEXT_PUBLIC_ENABLE_REUSE_TARGET. This is read at
 * build time, not runtime — NEXT_PUBLIC_* values are inlined by Next, so
 * flipping it needs a rebuild, not just a restart.
 *
 * While dark mode is in beta the flag is necessary but not sufficient: it
 * releases the pin only for `isThemeBetaUser` (below). All consumers read
 * the same pair, which is why this lives in one place:
 *   - `components/RouteThemeProvider.tsx` pins `forcedTheme="light"` unless
 *     the flag is on *and* the person is in the beta
 *   - the Appearance card on `/c/<client>/settings` (Profile) and the
 *     what's-new prompt render only on the same condition — a visible
 *     control that could not change anything is worse than no control
 *
 * THERE IS NOW A SECOND WAY OUT OF THE PIN, and it is a deliberate widening of
 * this policy rather than an application of it. `Providers` also releases the
 * pin for anyone on `DS_PREVIEW_EMAILS` (see `lib/dsPreview.ts`), on every
 * route rather than only the prototypes.
 *
 * The property this flag protects is that nobody unprepared lands on a
 * half-converted page. This switch was the only tool for that when it was
 * written — environment-wide and build-time, so the choice was all users or
 * none. A server-resolved per-user allowlist did not exist yet. It does now,
 * and it holds the same property by naming the people who asked to see the
 * unfinished work instead of naming the routes where unfinished work lives.
 *
 * What that costs: dark mode is no longer strictly route-scoped, so an
 * allowlisted person sees the ~560 files that still paint fixed colours
 * wherever they go. That is the point for them and would be a bug for anyone
 * else, which is why the list is the gate.
 *
 * THE THIRD WAY OUT IS BY ROUTE, and it is the first one that names where
 * finished work lives rather than who may see unfinished work. The creator
 * surfaces (`/one`, `/join`) wear the candidate design system outright and
 * have no fixed-colour debt: their dark ground is complete, and the portal's
 * Settings sheet already offers Light, Dark and System. On those routes the
 * pin is released for everyone, so a creator's choice, or their OS, applies.
 * Everywhere else the pin holds exactly as before. `RouteThemeProvider` is
 * the one consumer; it reads the pathname and asks `isThemePinned`.
 */
export const THEME_SWITCHER_ENABLED =
  process.env.NEXT_PUBLIC_ENABLE_THEME_SWITCHER === "true";

/**
 * While dark mode is in beta the build flag opens it for the NewForm team
 * only, by email domain. `RouteThemeProvider` pins light for everyone else
 * even with the flag on, the Appearance card on Settings > Profile renders
 * only for them, and the what's-new prompt announces it only to them. One
 * rule, read in those three places, so the pin, the control and the
 * announcement can never disagree about who is in the beta.
 *
 * Both domains, because #1390 moves people from @newform.ai to @newform.com
 * one confirmation at a time and a half-moved team is still one team.
 *
 * A display gate, not an authorization boundary: it decides which theme a
 * page wears and nothing else. Never reuse it to grant access to data or
 * actions; those go through `lib/auth` on the backend.
 */
const THEME_BETA_EMAIL_DOMAINS = ["newform.com", "newform.ai"] as const;

export function isThemeBetaUser(email: string | null | undefined): boolean {
  if (!email) return false;
  const domain = email.trim().toLowerCase().split("@").pop();
  return THEME_BETA_EMAIL_DOMAINS.some((allowed) => domain === allowed);
}

/**
 * Whether this person gets the dark-mode beta: the flag is on and they are
 * in it. Resolved once, in the root layout, and handed to the client as one
 * boolean (`useThemeBeta`), so the pin, the Appearance card and the
 * announcement read the same answer rather than each re-deriving it from a
 * session shape that could drift. `switcherEnabled` is a parameter only so
 * the rule can be tested with the build-time flag in either state.
 */
export function resolveThemeBeta(
  email: string | null | undefined,
  switcherEnabled: boolean = THEME_SWITCHER_ENABLED,
): boolean {
  return switcherEnabled && isThemeBetaUser(email);
}

/**
 * Whether to force light for this render: the pin holds unless one of the
 * three ways out applies (the beta, the preview allowlist, a creator route).
 * Pure so the case the beta exists for, the flag on and the person outside
 * it, can be pinned by a test.
 */
export function shouldPinLight({
  pathname,
  dsPreview,
  themeBeta,
}: {
  pathname: string | null | undefined;
  dsPreview: boolean;
  themeBeta: boolean;
}): boolean {
  return !themeBeta && !dsPreview && isThemePinned(pathname);
}

/**
 * The routes whose layouts wear `ds-next` and are fully themed. Keep this in
 * step with the layouts that add that class (`app/one/layout.tsx`,
 * `app/join/layout.tsx`): a route listed here renders dark as finished, so it
 * must be.
 */
export const THEME_UNPINNED_ROUTES = ["/one", "/join"] as const;

/**
 * The three answers to "which theme", in the order every chooser shows them.
 * `system` follows the OS; the other two pin a choice in localStorage.
 * next-themes stores the id, so the ids are its vocabulary, not ours.
 */
export const THEME_OPTIONS = [
  { id: "light", name: "Light" },
  { id: "dark", name: "Dark" },
  { id: "system", name: "System" },
] as const;

export type ThemeId = (typeof THEME_OPTIONS)[number]["id"];

export const isThemeId = (value: string | undefined): value is ThemeId =>
  THEME_OPTIONS.some((option) => option.id === value);

/**
 * Whether production pins this path to light.
 *
 * A prefix match on a path segment, so `/one/videos` is the portal and
 * `/onesie` is not. A missing pathname (never the case in the App Router, but
 * the hook's type allows it) pins, since pinning is the safe side.
 */
export function isThemePinned(pathname: string | null | undefined): boolean {
  if (!pathname) return true;
  return !THEME_UNPINNED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}
